import { STDocument } from '@st/sdk'
import { Button, Dialog, DialogButtons, Select, TextArea } from '@st/theme'
import { useReducer } from 'react'
import { match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { RadioSelect } from '../ui-components'
import { selDocumentChecklistItems, selDocumentTypeOptions } from './st-folder-selectors'
import { formatChecklistItem } from '~/features/checklists/model'

type Props = {
  folderId: string
  doc: STDocument
  onClose: () => void
}

export function ReportDocumentIssueDialog({ folderId, doc, onClose }: Props) {
  const stFolder = useProcess('stFolder')
  const documentTypes = useSelect('stFolder', selDocumentTypeOptions)
  const checklistItems = useSelect('stFolder', selDocumentChecklistItems)

  const defaultChecklistItemId = checklistItems.find((item) =>
    item.attachments.some((attachment) => attachment.documentId == doc.id)
  )?.id
  const [state, dispatch] = useReducer(reducer, {
    issueType: undefined,
    issueDescription: '',
    expectedDocumentTypeId: doc.documentTypeId,
    expectedChecklistItemId: defaultChecklistItemId
  })
  const page = state.issueType ? 'details' : 'issueType'

  function onClickReport() {
    if (!canSubmit(state)) {
      return
    }
    const issueType = state.issueType!
    const expectedDocumentTypeId =
      issueType == 'miscategorized' ? state.expectedDocumentTypeId : undefined
    const expectedChecklistItemId =
      issueType == 'missed_checklist_item' ? state.expectedChecklistItemId : undefined
    stFolder.send({
      type: 'reportDocumentIssue',
      request: {
        type: 'folders/reportDocumentIssue',
        folderId: folderId,
        documentId: doc.id,
        issueType: issueType,
        issueDescription: state.issueDescription,
        expectedDocumentTypeId: expectedDocumentTypeId,
        expectedChecklistItemId: expectedChecklistItemId
      },
      documentName: doc.name
    })
    onClose()
  }

  const isReportDisabled = !canSubmit(state)

  return (
    <Dialog
      title="Report document issue"
      subtitle="If something didn't work as expected, please let us know. We use this information to improve our algorithms and release new updates every week."
      buttons={match(page)
        .with('issueType', () => (
          <DialogButtons>
            <Button variant="subtle" onClick={() => onClose()}>
              Cancel
            </Button>
          </DialogButtons>
        ))
        .with('details', () => (
          <DialogButtons>
            <Button variant="subtle" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button variant="primary" disabled={isReportDisabled} onClick={onClickReport}>
              Report
            </Button>
          </DialogButtons>
        ))
        .exhaustive()}
    >
      {match(page)
        .with('issueType', () => (
          <RadioSelect
            options={ISSUE_TYPE_OPTIONS}
            value={state.issueType}
            onChange={(value) => {
              dispatch({ type: 'selectIssueType', issueType: value as IssueType })
            }}
          />
        ))
        .with('details', () => (
          <div className="flex flex-col gap-3">
            {state.issueType == 'miscategorized' && (
              <div className="flex flex-col gap-2">
                <label className="font-semibold">Expected document type</label>
                <Select
                  options={documentTypes}
                  buildValue={(dt) => dt.id}
                  buildLabel={(dt) => dt.name}
                  value={state.expectedDocumentTypeId}
                  onChange={(value) =>
                    dispatch({ type: 'setExpectedDocumentTypeId', value: value })
                  }
                  ariaLabel="Expected document type"
                />
              </div>
            )}

            {state.issueType == 'missed_checklist_item' && (
              <div className="flex flex-col gap-2">
                <label className="font-semibold">Expected checklist item</label>
                <Select
                  options={checklistItems}
                  buildValue={(item) => item.id}
                  buildLabel={(item) => formatChecklistItem(item)}
                  value={state.expectedChecklistItemId}
                  onChange={(value) =>
                    dispatch({ type: 'setExpectedChecklistItemId', value: value })
                  }
                  ariaLabel="Expected checklist item"
                />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="font-semibold">Additional context</label>
              <TextArea
                placeholder="If there is more context to share, please elaborate here."
                value={state.issueDescription}
                onChange={(value) => dispatch({ type: 'setIssueDescription', value: value })}
              />
            </div>
          </div>
        ))
        .exhaustive()}
    </Dialog>
  )
}

type IssueType = 'miscategorized' | 'not_viewable' | 'missed_checklist_item' | 'other'
type Page = 'issueType' | 'details'
type IssueTypeOption = { label: string; key: IssueType }

const ISSUE_TYPE_OPTIONS: IssueTypeOption[] = [
  { label: 'Document is not viewable', key: 'not_viewable' },
  { label: 'Document was miscategorized', key: 'miscategorized' },
  {
    label: 'Document did not auto-complete the checklist item',
    key: 'missed_checklist_item'
  },
  { label: 'Other', key: 'other' }
]

type DialogState = {
  issueType?: IssueType
  issueDescription: string
  expectedDocumentTypeId?: string
  expectedChecklistItemId?: string
}

type Action =
  | { type: 'selectIssueType'; issueType: IssueType }
  | { type: 'setIssueDescription'; value: string }
  | { type: 'setExpectedDocumentTypeId'; value?: string }
  | { type: 'setExpectedChecklistItemId'; value?: string }

function reducer(state: DialogState, action: Action): DialogState {
  return match<Action, DialogState>(action)
    .with({ type: 'selectIssueType' }, ({ issueType }) => ({
      ...state,
      issueType: issueType
    }))
    .with({ type: 'setIssueDescription' }, ({ value }) => ({
      ...state,
      issueDescription: value
    }))
    .with({ type: 'setExpectedDocumentTypeId' }, ({ value }) => ({
      ...state,
      expectedDocumentTypeId: value
    }))
    .with({ type: 'setExpectedChecklistItemId' }, ({ value }) => ({
      ...state,
      expectedChecklistItemId: value
    }))
    .otherwise(() => state)
}

function canSubmit(state: DialogState) {
  if (!state.issueType) {
    return false
  }
  return match(state.issueType)
    .with('miscategorized', () => !!state.expectedDocumentTypeId)
    .with('missed_checklist_item', () => !!state.expectedChecklistItemId)
    .otherwise(() => true)
}
